import {
  AppShell,
  Avatar,
  Badge,
  Burger,
  Button,
  Center,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuilding,
  IconBuildingCommunity,
  IconCalendarEvent,
  IconCpu,
  IconFileCertificate,
  IconHistory,
  IconHome,
  IconLogout,
  IconPlugConnected,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { type Area, areasFor } from './access.ts';
import { api, unwrap } from './api.ts';
import { signOut } from './auth.ts';
import { initials, roleLabel } from './format.ts';
import { setSentryTags, setSentryUser } from './errorReporting.ts';
import { Failure, Loading, useMe } from './ui.tsx';

const NAV: Record<
  Area,
  { to: string; label: string; icon: ReactNode; group: 'Workspace' | 'People' | 'Platform' }
> = {
  home: { to: '/', label: 'Overview', icon: <IconHome size={18} />, group: 'Workspace' },
  buildings: { to: '/buildings', label: 'Buildings', icon: <IconBuilding size={18} />, group: 'Workspace' },
  bookings: { to: '/bookings', label: 'Bookings', icon: <IconCalendarEvent size={18} />, group: 'Workspace' },
  devices: { to: '/devices', label: 'Devices', icon: <IconCpu size={18} />, group: 'Workspace' },
  users: { to: '/users', label: 'Users', icon: <IconUsers size={18} />, group: 'People' },
  departments: {
    to: '/departments',
    label: 'Departments',
    icon: <IconUsersGroup size={18} />,
    group: 'People',
  },
  organizations: {
    to: '/organizations',
    label: 'Organizations',
    icon: <IconBuildingCommunity size={18} />,
    group: 'Platform',
  },
  contracts: {
    to: '/contracts',
    label: 'Contracts',
    icon: <IconFileCertificate size={18} />,
    group: 'Platform',
  },
  integrations: {
    to: '/integrations',
    label: 'Integrations',
    icon: <IconPlugConnected size={18} />,
    group: 'Platform',
  },
  audit: { to: '/audit', label: 'Audit log', icon: <IconHistory size={18} />, group: 'Platform' },
};

const GROUPS = ['Workspace', 'People', 'Platform'] as const;

function NoAccess() {
  return (
    <Center h="100vh">
      <Stack align="center" gap="sm" maw={420} ta="center">
        <Title order={3}>This console is for admins</Title>
        <Text c="dimmed">
          Your user has no admin role. Ask an admin of your organization to make you one, or use the Budovan
          app.
        </Text>
        <Button variant="default" leftSection={<IconLogout size={16} />} onClick={() => void signOut()}>
          Sign out
        </Button>
      </Stack>
    </Center>
  );
}

export function Shell() {
  const [opened, { toggle, close }] = useDisclosure();
  const me = useMe();
  const organizations = useQuery({
    queryKey: ['organizations'],
    queryFn: () => unwrap(api().GET('/v1/organizations')),
    enabled: Boolean(me.data),
  });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    if (!me.data) return;
    setSentryUser({ id: me.data.id });
    setSentryTags({
      tenant: me.data.organizationId,
      role: me.data.role,
      operator: String(me.data.isOperator),
    });
  }, [me.data]);

  if (me.isPending) return <Loading />;
  if (me.error) return <Failure error={me.error} />;
  const areas = areasFor(me.data);
  if (areas.length === 0) return <NoAccess />;
  const organization = organizations.data?.find((candidate) => candidate.id === me.data.organizationId);
  const active = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 248, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="xl"
    >
      <AppShell.Header px="lg">
        <Group h="100%" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menu" />
            <Text fw={700} size="lg">
              Budovan
            </Text>
            <Badge variant="default" radius="sm">
              Admin
            </Badge>
          </Group>
          <Menu position="bottom-end" width={240}>
            <Menu.Target>
              <UnstyledButton aria-label="Account menu">
                <Group gap="sm">
                  <div style={{ textAlign: 'right' }}>
                    <Text size="sm" fw={600}>
                      {me.data.displayName ?? me.data.email}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {organization?.name ?? 'Your organization'}
                    </Text>
                  </div>
                  <Avatar color="ink" radius="xl">
                    {initials(me.data.displayName, me.data.email)}
                  </Avatar>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{roleLabel(me.data)}</Menu.Label>
              <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void signOut()}>
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="sm">
        <ScrollArea>
          <Stack gap="lg">
            {GROUPS.map((group) => {
              const items = areas.filter((area) => NAV[area].group === group);
              if (items.length === 0) return null;
              return (
                <Stack gap={2} key={group}>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="sm" mb={4}>
                    {group}
                  </Text>
                  {items.map((area) => (
                    <NavLink
                      key={area}
                      component={Link}
                      to={NAV[area].to}
                      label={NAV[area].label}
                      leftSection={NAV[area].icon}
                      active={active(NAV[area].to)}
                      onClick={close}
                      variant="filled"
                      style={{ borderRadius: 'var(--mantine-radius-md)' }}
                    />
                  ))}
                </Stack>
              );
            })}
          </Stack>
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main bg="gray.0">
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
